import { Suspense } from 'react';
import { Spin } from 'antd';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { keyboardRoutes, publicRoutes, standardRoutes } from './routes';
import { getPathAccessState } from './accessControl';
import { isFeatureEnabled } from './routeGuards';
import RouteErrorBoundary from '../../shared/ui/errors/RouteErrorBoundary';
import { useAuthSession } from '../providers/AuthSessionProvider';
import { useAdminSession } from '../providers/AdminSessionProvider';
import { useCustomerPortalSession } from '../providers/CustomerPortalSessionProvider';
import { ENABLE_AUTH } from '../../shared/utils/constants';

function RouteLoadingFallback() {
  return (
    <div style={{ minHeight: 240, display: 'grid', placeItems: 'center' }}>
      <Spin size="large" />
    </div>
  );
}

function GuardedRouteElement({
  Component,
  feature,
  routePath,
  scope,
  sessionScope = 'workspace',
  isPublic = false,
  roles = [],
}) {
  const location = useLocation();
  const { session, isReady: isWorkspaceAuthReady = true } = useAuthSession();
  const { session: adminSession, isReady: isAdminAuthReady = true } = useAdminSession();
  const { session: customerSession, isReady: isCustomerAuthReady = true } = useCustomerPortalSession();
  const hasWorkspaceSession = Boolean(session?.accessToken);
  const hasAdminSession = Boolean(adminSession?.accessToken);
  const hasCustomerSession = Boolean(customerSession?.accessToken);
  const authReady =
    sessionScope === 'customer'
      ? isCustomerAuthReady
      : sessionScope === 'admin'
        ? isAdminAuthReady
        : isWorkspaceAuthReady;
  const hasSession =
    sessionScope === 'customer'
      ? hasCustomerSession
      : sessionScope === 'admin'
        ? hasAdminSession
        : hasWorkspaceSession;
  const currentRole = session?.role;
  const customerNextPath = `${location.pathname}${location.search || ''}`;
  const workspaceAccessState =
    ENABLE_AUTH && !isPublic && sessionScope === 'workspace'
      ? getPathAccessState(routePath, currentRole, {
          enableAuth: true,
          permissions: session?.permissions,
          authResolved: isWorkspaceAuthReady,
        })
      : { status: 'allowed' };

  if (ENABLE_AUTH && !isPublic && !hasSession) {
    if (!authReady) {
      return <RouteLoadingFallback />;
    }

    if (sessionScope === 'customer') {
      return (
        <Navigate
          to={`/customer-portal/auth/sign-in?next=${encodeURIComponent(customerNextPath)}`}
          replace
        />
      );
    }

    if (sessionScope === 'admin') {
      return <Navigate to={`/admin/login?next=${encodeURIComponent(customerNextPath)}`} replace />;
    }

    return <Navigate to={`/login?next=${encodeURIComponent(customerNextPath)}`} replace />;
  }

  if (workspaceAccessState.status === 'loading') {
    return <RouteLoadingFallback />;
  }

  if (workspaceAccessState.status === 'denied') {
    const fallbackPath = routePath.startsWith('/settings/admin') ? '/settings' : '/invoices';
    return <Navigate to={fallbackPath} replace />;
  }

  if (
    ENABLE_AUTH &&
    !isPublic &&
    sessionScope !== 'workspace' &&
    Array.isArray(roles) &&
    roles.length &&
    (!currentRole || !roles.includes(currentRole))
  ) {
    const fallbackPath = routePath.startsWith('/settings/admin') ? '/settings' : '/invoices';
    return <Navigate to={fallbackPath} replace />;
  }

  if (
    ENABLE_AUTH &&
    isPublic &&
    hasAdminSession &&
    routePath === '/admin/login'
  ) {
    return <Navigate to="/admin" replace />;
  }

  if (
    ENABLE_AUTH &&
    isPublic &&
    hasWorkspaceSession &&
    ['/login', '/sample-portal'].includes(routePath)
  ) {
    return <Navigate to="/dashboard" replace />;
  }

  if (
    ENABLE_AUTH &&
    isPublic &&
    hasCustomerSession &&
    routePath === '/customer-portal/auth/sign-in/*'
  ) {
    return <Navigate to="/customer-portal" replace />;
  }

  return (
    <RouteErrorBoundary routePath={routePath} feature={feature} scope={scope}>
      <Suspense fallback={<RouteLoadingFallback />}>
        <Component />
      </Suspense>
    </RouteErrorBoundary>
  );
}

function renderRoute(route, nested = false) {
  const Component = route.element;
  const routePath = nested ? `/keyboard${route.path === '/' ? '' : route.path}` : route.path;
  const scope = nested ? 'keyboard-route' : route.public ? 'public-route' : 'standard-route';
  const guardedElement = (
    <GuardedRouteElement
      Component={Component}
      routePath={routePath}
      feature={route.feature || route.key}
      scope={scope}
      sessionScope={route.sessionScope || 'workspace'}
      isPublic={Boolean(route.public)}
      roles={route.roles}
    />
  );

  if (nested && route.path === '/') {
    return <Route key="nested:index" index element={guardedElement} />;
  }
  const path = nested ? route.path.replace(/^\//, '') : route.path;
  return <Route key={`${nested ? 'nested' : 'root'}:${route.path}`} path={path} element={guardedElement} />;
}

function KeyboardRoutesRenderer() {
  const enabledKeyboardRoutes = keyboardRoutes.filter((route) => isFeatureEnabled(route.feature));
  return (
    <Routes>
      {enabledKeyboardRoutes.map((route) => renderRoute(route, true))}
      <Route path="*" element={<Navigate to="/keyboard" replace />} />
    </Routes>
  );
}

export default function AppRouter() {
  const enabledStandardRoutes = standardRoutes.filter((route) => isFeatureEnabled(route.feature));
  const enabledPublicRoutes = publicRoutes.filter((route) => isFeatureEnabled(route.feature));

  return (
    <Routes>
      <Route path="/" element={<Navigate to="/landing" replace />} />
      {enabledPublicRoutes.map((route) => renderRoute(route))}
      {enabledStandardRoutes.map((route) => renderRoute(route))}
      <Route path="/keyboard/*" element={<KeyboardRoutesRenderer />} />
      <Route path="*" element={<Navigate to={ENABLE_AUTH ? '/landing' : '/'} replace />} />
    </Routes>
  );
}
